import { SyncOutlined } from '@ant-design/icons';
import { Button, Divider, Modal, notification, Tour, message } from 'antd';
import { observer } from 'mobx-react';
import { useState, useRef } from 'react';
import AiGenTip from '../../../components/aiGenTip';
import { summaryStore } from '../store';
import { Summary } from '../Summary/index';
import styles from './index.module.scss';

const Step2 = observer(() => {
    const [modal, contextHolder] = Modal.useModal();
    const [notiApi, notiContextHolder] = notification.useNotification();
    const [tourOpen, setTourOpen] = useState(false);
    const [tourSteps, setTourSteps] = useState([]);
    const [hasSeenGuide, setHasSeenGuide] = useState(false); // 是否已经看过指引
    const previewCalledRef = useRef(false); // 防止重复调用 proceedWithPreview

    // 检查是否有选择额外服务（图、代码、公式、表）
    const hasSelectedExtraServices = () => {
        if (!summaryStore.paragraphs || summaryStore.paragraphs.length === 0) {
            return false;
        }

        const checkChapter = (chapter) => {
            // 检查章节本身是否有选择额外服务（排除 NORMAL）
            if (chapter.selected_extra) {
                const selected = chapter.selected_extra.split(',').filter(Boolean);
                const hasExtra = selected.some((type) => type !== 'NORMAL');
                if (hasExtra) return true;
            }

            // 检查章节本身是否有选择图表类型
            if (chapter.selectedChartType) {
                return true;
            }

            // 检查二级标题
            if (chapter.sections && chapter.sections.length > 0) {
                for (const section of chapter.sections) {
                    if (checkSection(section)) return true;
                }
            }

            return false;
        };

        const checkSection = (section) => {
            // 检查二级标题本身
            if (section.selected_extra) {
                const selected = section.selected_extra.split(',').filter(Boolean);
                const hasExtra = selected.some((type) => type !== 'NORMAL');
                if (hasExtra) return true;
            }

            // 检查二级标题是否有选择图表类型
            if (section.selectedChartType) {
                return true;
            }

            // 检查三级标题
            if (section.subsections && section.subsections.length > 0) {
                for (const subsection of section.subsections) {
                    if (subsection.selected_extra) {
                        const selected = subsection.selected_extra.split(',').filter(Boolean);
                        const hasExtra = selected.some((type) => type !== 'NORMAL');
                        if (hasExtra) return true;
                    }

                    // 检查三级标题是否有选择图表类型
                    if (subsection.selectedChartType) {
                        return true;
                    }
                }
            }

            return false;
        };

        // 遍历所有一级标题
        for (const outline of summaryStore.paragraphs) {
            if (checkChapter(outline)) {
                return true;
            }
        }

        return false;
    };

    // 收集所有可用的图标元素用于 Tour 引导
    const collectAvailableIcons = () => {
        const iconTypes = ['TABLE', 'DIAGRAM', 'FORMULA', 'CODE', 'DATA'];
        const iconLabels = {
            TABLE: '数据(表)',
            DIAGRAM: '图',
            FORMULA: '公式',
            CODE: '代码',
            DATA: '数据',
        };

        // 等待 DOM 更新后查找元素
        setTimeout(() => {
            const steps = [];

            iconTypes.forEach((type) => {
                const elements = document.querySelectorAll(`[data-tour-icon="${type}"]`);
                if (elements.length > 0) {
                    // 找到第一个可见的元素
                    const visibleElement = Array.from(elements).find((el) => {
                        const rect = el.getBoundingClientRect();
                        return rect.width > 0 && rect.height > 0;
                    });

                    if (visibleElement || elements[0]) {
                        const targetElement = visibleElement || elements[0];
                        steps.push({
                            title: `添加${iconLabels[type]}`,
                            description: `点击图标可以为章节添加${iconLabels[type]}，让您的文档更加丰富`,
                            target: () => targetElement,
                            placement: 'right',
                        });
                    }
                }
            });

            if (steps.length > 0) {
                setTourSteps(steps);
                setTourOpen(true);
            } else {
                // 如果没有找到可用的图标，提示用户并继续预览流程
                message.info('当前没有可添加额外服务的章节，请先添加章节内容');
                proceedWithPreview();
            }
        }, 100);
    };

    async function genAll() {
        console.log(
            'summaryStore.customReference.customReferenceDocument',
            summaryStore.customReference.customReferenceDocument,
        );
        try {
            if (!summaryStore.isCheckConfirm) {
                const config = {
                    title: '提示',
                    content: (
                        <div>平台所生成的全文为范文，仅用作参考，不用作毕业全文、发表刊物等</div>
                    ),
                    okText: '同意',
                };
                await modal.warning(config);
            }

            // 检查是否有选择额外服务
            const hasExtra = hasSelectedExtraServices();
            if (!hasExtra) {
                // 检查是否已经看过指引
                if (hasSeenGuide) {
                    // 已经看过指引，直接继续预览流程
                    proceedWithPreview();
                } else {
                    // 首次点击，弹出确认提示
                    modal.confirm({
                        title: '提示',
                        content: '您还没有选择图、代码、公式、表、数据等额外服务，是否需要添加？',
                        okText: '是',
                        cancelText: '否',
                        onOk: () => {
                            // 用户点击"是"，启动 Tour 引导
                            collectAvailableIcons();
                        },
                        onCancel: () => {
                            // 用户点击"否"，标记已经看过指引，然后继续预览流程
                            setHasSeenGuide(true);
                            proceedWithPreview();
                        },
                    });
                }
            } else {
                // 已经选择了额外服务，直接继续
                proceedWithPreview();
            }
        } catch (error) {
            console.error(error);
        }
    }

    // 继续预览的逻辑
    const proceedWithPreview = async () => {
        // 防止重复调用
        if (previewCalledRef.current) {
            return;
        }
        previewCalledRef.current = true;

        modal.confirm({
            title: '提示',
            content: '请确认您的大纲和参考文献无误。提交后，生成长文将无法修改大纲和参考文献',
            okText: '同意',
            onOk: async () => {
                const inviter = localStorage.getItem('inviter');

                await summaryStore.getPreview({ inviter });
                // 滚动到页面顶部
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth', // 使用平滑滚动效果
                });
                // 重置标志位，允许下次调用
                previewCalledRef.current = false;
            },
            onCancel: () => {
                // 用户取消时也重置标志位
                previewCalledRef.current = false;
            },
        });
    };
    return (
        <>
            {contextHolder}
            {notiContextHolder}
            <>
                {/*<div id="summaryHeader" className={styles.sectionTitle}>*/}
                {/*    编辑大纲，生成全文*/}
                {/*</div>*/}
                {/*<div className={styles.sectionDesc}>*/}
                {/*    🔔 点击小节右侧按钮，为该小节增加相应的*/}
                {/*    <span>表格，图，公式，代码段</span>*/}
                {/*    ，对该小节进行<span>增加、删减、修改</span>*/}
                {/*</div>*/}
                <div className={styles.docTitle}>题目：{summaryStore.majorName}</div>
                {/* <div className={styles.docType}>科目：11</div> */}
                <Divider
                    dashed={true}
                    style={{
                        borderColor: '#EEEEF1',
                    }}
                />
                <Summary />
                <Divider
                    dashed={true}
                    style={{
                        borderColor: '#EEEEF1',
                    }}
                />

                <Button
                    shape="round"
                    type="primary"
                    size="large"
                    onClick={genAll}
                    loading={summaryStore.isLoadingPreview}
                    style={{ width: '180px' }}
                >
                    预览
                </Button>
                <Button
                    size="large"
                    type="text"
                    className={styles.regenBtn}
                    onClick={() => {
                        if (summaryStore.currentStep === 3) {
                            summaryStore.step3Submit(notiApi);
                        } else {
                            summaryStore.getSummary(notiApi);
                        }
                    }}
                >
                    <SyncOutlined />
                    大纲不满意？重新生成
                </Button>
                <AiGenTip />
            </>
            <Tour
                open={tourOpen}
                onClose={() => {
                    setTourOpen(false);
                    // 用户关闭 Tour 时，不自动继续预览流程，让用户自己决定
                }}
                steps={tourSteps}
                onFinish={() => {
                    setTourOpen(false);
                    // 标记已经看过指引
                    setHasSeenGuide(true);
                }}
            />
        </>
    );
});

export default Step2;
